/**
 * Comparte qué páginas tienen una petición de IA en marcha entre la navegación, la barra
 * de dirección y el panel. El estado usa runas para actualizar esos componentes.
 *
 * Al entrar consulta el estado del servidor, incluida la ausencia de peticiones después
 * de un reinicio. Los oyentes de progreso lo actualizan al comenzar y terminar, incluso
 * si el panel se desmonta. Así no hace falta consultar periódicamente.
 *
 * Se cuenta por petición: la página donde se pidió y las que va creando o escribiendo.
 * Todas llevan la señal, y el panel de cualquier otra conversación queda bloqueado.
 */
import type {
  AiActiveRun,
  AiProgress,
  HtmlSource,
  McpEvent,
  McpTurnEnd,
  McpTurnInfo,
} from "@shared/types";

import { api, post, stream } from "./pb";

/**
 * Una petición en marcha: donde se pidió --la página de su conversación, o
 * `BUILD_MODE`-- y las otras páginas que creó o está escribiendo.
 */
export interface AiWork {
  anchor: string;
  pages: string[];
}

/** Lo que hay en marcha en la aplicación, una entrada por petición. */
let runs = $state<AiWork[]>([]);
/** El turno MCP abierto en la aplicación, si un asistente externo trabaja en ella. */
let mcpTurn = $state<McpTurnInfo | null>(null);
/** Cuántas veces el asistente cambió páginas o tablas: quien las muestra las relee. */
let mcpChanges = $state(0);
/** Una prueba de página que pide el asistente, esperando a que alguien la haga. */
let mcpProbe = $state<{
  probeId: string;
  hash: string;
  page: string;
  fuentes?: HtmlSource[];
} | null>(null);
/** El último cierre de un turno, para avisar con su resumen. */
let mcpClosed = $state<{ motivo: McpTurnEnd; resumen?: string; at: number } | null>(null);
/** Se pulsó algo bloqueado: el diálogo «¿Esperar o interrumpir?» está abierto. */
let mcpAsking = $state(false);
/** La aplicación cuyo hilo MCP está abierto, y cómo cortarlo. */
let mcpScope = "";
let mcpStop: AbortController | null = null;
let mcpRetry: ReturnType<typeof setTimeout> | null = null;
/** De que aplicación es lo que hay apuntado: cambiar de aplicación lo vacía. */
let scope = "";
let scopeEpoch = 0;

/**
 * Las peticiones de otras páginas a las que esta pestaña ya está enganchada,
 * para no abrir dos hilos sobre la misma.
 */
const followed = new Map<string, AbortController>();

export const aiActivity = {
  /** Las páginas donde la IA trabaja, por id: donde se pidió y las que escribe. */
  get pages(): string[] {
    return [...new Set(runs.flatMap((run) => [run.anchor, ...run.pages]))];
  },
  /** Hay algo en marcha en la aplicación. */
  get busy(): boolean {
    return runs.length > 0;
  },
  /** La IA trabaja en esta página, se pidiera ahí o desde otra conversación. */
  has(pageId: string): boolean {
    return runs.some((run) => run.anchor === pageId || run.pages.includes(pageId));
  },
  /** Hay una petición pedida desde aquí: la de esta conversación. */
  askedFrom(pageId: string): boolean {
    return runs.some((run) => run.anchor === pageId);
  },
  /** La petición pedida desde `anchor`, con las páginas que trabaja. */
  of(anchor: string): AiWork | null {
    return runs.find((run) => run.anchor === anchor) ?? null;
  },
  /**
   * La petición que estorba a la conversación de `here`: una pedida en otro
   * sitio. El panel bloquea contra esta, aunque esté escribiendo `here`.
   */
  elsewhere(here: string): AiWork | null {
    return runs.find((run) => run.anchor !== here) ?? null;
  },
  /** El modo MCP abierto: quién y desde cuándo. Mientras lo haya, el panel solo mira. */
  get mcp(): McpTurnInfo | null {
    return mcpTurn;
  },
  /** Sube cada vez que el asistente cambió páginas o tablas. */
  get mcpChanges(): number {
    return mcpChanges;
  },
  /** La prueba de página que pide el asistente, si hay una. */
  get mcpProbe() {
    return mcpProbe;
  },
  /** Cómo terminó el último turno MCP visto desde esta pestaña. */
  get mcpClosed() {
    return mcpClosed;
  },
  /** El diálogo «¿Esperar o interrumpir?» está abierto. */
  get mcpAsking(): boolean {
    return mcpAsking;
  },
};

/**
 * Se intentó algo que el modo MCP no deja: se pregunta si esperar o
 * interrumpir. Devuelve si el modo está abierto, para que quien llama se pare.
 */
export function askMcpWait(): boolean {
  if (!mcpTurn) return false;
  mcpAsking = true;
  return true;
}

/** Cerrar el diálogo «¿Esperar o interrumpir?». */
export function closeMcpWait(): void {
  mcpAsking = false;
}

/** Empieza una petición pedida desde `anchor`. */
export function startAiRun(anchor: string, appId = scope): void {
  if (appId !== scope) return;
  if (!anchor || runs.some((run) => run.anchor === anchor)) return;
  runs = [...runs, { anchor, pages: [] }];
}

/** La petición de `anchor` trabaja ahora en estas páginas, además de la suya. */
export function touchAiRun(anchor: string, pages: string[], appId = scope): void {
  if (appId !== scope) return;
  const now = runs.find((run) => run.anchor === anchor);
  const next = pages.filter((id) => id && id !== anchor);
  if (!now) {
    runs = [...runs, { anchor, pages: next }];
    return;
  }
  if (next.length === now.pages.length && next.every((id, i) => id === now.pages[i])) return;
  runs = runs.map((run) => (run.anchor === anchor ? { ...run, pages: next } : run));
}

/** Terminó la petición de `anchor`: sus páginas dejan de llevar la señal. */
export function endAiRun(anchor: string, appId = scope): void {
  if (appId !== scope) return;
  if (!runs.some((run) => run.anchor === anchor)) return;
  runs = runs.filter((run) => run.anchor !== anchor);
}

/** Empezar de cero. Al cambiar de aplicación lo de la anterior no cuenta. */
function resetAiActivity(): void {
  runs = [];
  scopeEpoch += 1;
  // Los hilos de la aplicación anterior no pueden reponer señales al cambiar de aplicación.
  for (const stop of followed.values()) stop.abort();
  followed.clear();
  scope = "";
}

/**
 * Preguntar al servidor que hay en marcha y engancharse a lo que no se esté
 * mirando ya.
 *
 * Se llama al entrar en la aplicación. Sin el segundo paso la señal viviría
 * solo hasta la primera recarga: el hilo que la mantenía al día se cortó con
 * la ventana anterior, y nadie volveria a contar que la petición terminó.
 *
 * `skip` es la página que se tiene abierta: de esa se encarga el panel, que
 * además la enseña avanzando. Engancharse aquí también no rompe nada --el
 * servidor admite varios oyentes-- pero sería un hilo de más.
 */
export async function seedAiActivity(appId: string, skip: string): Promise<void> {
  if (scope !== appId) resetAiActivity();
  scope = appId;
  const epoch = scopeEpoch;

  let active: AiActiveRun[];
  try {
    active = await api<AiActiveRun[]>(`/api/apps/${appId}/ia/enMarcha`);
  } catch {
    // Sin respuesta no se inventa una señal: lo que se sepa por el hilo de
    // avisos sigue valiendo, y lo demás se sabra al volver a entrar.
    return;
  }
  if (scope !== appId || scopeEpoch !== epoch) return;

  // El turno MCP llega en la misma lista, sin página: no es una petición del panel.
  const pageRuns = active.filter((run) => run.kind !== "mcp" && run.page);
  runs = pageRuns.map((run) => ({ anchor: run.page, pages: run.pages ?? [] }));
  for (const run of pageRuns) {
    if (run.page === skip) continue;
    followRun(appId, run.page);
  }
}

/**
 * Mirar de lejos una petición de otra página: solo para saber en que páginas
 * trabaja y cuando termina.
 *
 * No recoge nada de lo que la IA escribe --eso es del panel de esa página, y
 * lo vuelve a pedir entero cuando se abra--. Lo único que hace aquí es llevar
 * la señal a las páginas que va tocando y apagarla en cuanto deja de haber
 * trabajo.
 */
function followRun(appId: string, pageId: string): void {
  const mark = `${appId}:${pageId}`;
  if (followed.has(mark)) return;
  const stop = new AbortController();
  followed.set(mark, stop);

  const release = () => {
    if (followed.get(mark) !== stop) return;
    followed.delete(mark);
    stop.abort();
    endAiRun(pageId, appId);
  };

  void stream<AiProgress>(
    `/api/apps/${appId}/paginas/${pageId}/ia/seguir`,
    {},
    (part) => {
      if (stop.signal.aborted) return;
      if (part.tipo === "paginas") touchAiRun(pageId, part.ids, appId);
      if (part.tipo === "fin" || part.tipo === "error") release();
    },
    stop.signal,
  )
    .then(release)
    .catch(release);
}

/* ------------------------------------------------------------------ */
/* Modo MCP                                                             */
/* ------------------------------------------------------------------ */

/** Lo que se espera antes de volver a abrir el hilo cuando se corta. */
const MCP_RETRY_MS = 3000;
/** El tope de esa espera cuando el hilo falla una y otra vez. */
const MCP_RETRY_MAX_MS = 60_000;

/**
 * Seguir el modo MCP de una aplicación: abrir un hilo con el servidor que
 * cuenta cuándo un asistente externo abre o cierra su turno, cuándo cambia
 * páginas o tablas, y cuándo quiere probar una página en este navegador.
 *
 * El hilo se vuelve a abrir solo si se corta --un reinicio del servidor lo
 * corta, y lo primero que llega al reabrirlo es cómo está el turno ahora--.
 * Si se cae sin haber traído nada --el servidor no responde o rechaza la
 * sesión--, cada intento espera el doble que el anterior, hasta un minuto.
 * Se llama al entrar en la aplicación; entrar en otra corta el de la anterior.
 */
export function followMcp(appId: string): void {
  if (mcpScope === appId && mcpStop) return;
  stopMcp();
  mcpScope = appId;
  const stop = new AbortController();
  mcpStop = stop;
  let wait = MCP_RETRY_MS;

  const open = () => {
    if (stop.signal.aborted) return;
    void stream<McpEvent>(
      `/api/apps/${appId}/mcp/seguir`,
      {},
      (event) => {
        if (stop.signal.aborted || mcpScope !== appId) return;
        // Llegó algo: el hilo funciona, y el próximo corte vuelve a la espera corta.
        wait = MCP_RETRY_MS;
        if (event.tipo === "turno") {
          mcpTurn = event.turno;
          if (!event.turno) mcpAsking = false;
        } else if (event.tipo === "cambio") mcpChanges += 1;
        else if (event.tipo === "probar")
          mcpProbe = {
            probeId: event.probeId,
            hash: event.hash,
            page: event.page,
            fuentes: event.fuentes,
          };
        else if (event.tipo === "cierre") {
          mcpClosed = { motivo: event.motivo, resumen: event.resumen, at: Date.now() };
          mcpChanges += 1;
        }
      },
      stop.signal,
    )
      .catch(() => {})
      .finally(() => {
        if (stop.signal.aborted) return;
        mcpRetry = setTimeout(() => {
          mcpRetry = null;
          open();
        }, wait);
        wait = Math.min(wait * 2, MCP_RETRY_MAX_MS);
      });
  };
  open();
}

/** Dejar de seguir el modo MCP: se salió de la aplicación. */
export function stopMcp(): void {
  // El reintento también retenía la aplicación anterior hasta vencer su espera.
  if (mcpRetry !== null) clearTimeout(mcpRetry);
  mcpRetry = null;
  mcpStop?.abort();
  mcpStop = null;
  mcpScope = "";
  mcpTurn = null;
  mcpProbe = null;
  mcpClosed = null;
  mcpAsking = false;
}

/** La prueba que pedía el asistente ya se hizo, o ya no se puede hacer. */
export function clearMcpProbe(): void {
  mcpProbe = null;
}

/** Interrumpir al asistente externo. Lo que esté haciendo termina y no puede seguir. */
export async function interruptMcp(appId: string): Promise<void> {
  await post(`/api/apps/${appId}/mcp/interrumpir`, {});
}
